import { useQuery } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import Skeleton from 'react-loading-skeleton';

import { AssignmentMessage } from '../../../../types/AssignmentMessage';
import { ContractMessage, ContractState } from '../../../../types/ContractMessage';
import { SupplierMessage } from '../../../../types/SupplierMessage';
import { Button } from '../../../cdl/Button/Button';
import { Checkbox } from '../../../cdl/Checkbox/Checkbox';
import { InputLabelText } from '../../../cdl/Input/components/InputLabelText';
import { Modal } from '../../../cdl/Modal/Modal';
import { Text } from '../../../cdl/Text/Text';
import { UpdateOrderRequest } from '../../../common/api/clients/order.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { FormikDebug } from '../../../common/form-elements/formik/FormikDebug';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { Box } from '../../../common/ui/Box';
import { useSpotAvailableAtPort } from '../../create-enquiry/hooks/useSpotAvailableAtPort';
import { toUpdateOrderRequest } from '../../model/order.mapper';
import { OrderModel } from '../../model/OrderModel';
import { groupContractsBySupplier, sortContractsForSupplier } from '../../util/contractSelection';
import { useOrderUpdate } from '../hooks/useOrderUpdate';

import { RequestSupplierOption, SupplierEntry } from './RequestSupplierOption';

interface RequestSuppliersModalProps {
    onClose: (hasError?: boolean) => void;
    order: OrderModel;
    assignments: AssignmentMessage[];
}

interface RequestSuppliersFormState {
    spot: boolean;
    selectedSupplierIds: string[];
    contractIdBySupplierId: Record<string, string>;
}

export const RequestSuppliersModal = (props: RequestSuppliersModalProps) => {
    const role = useRole();

    const spotAvailableAtPortQuery = useSpotAvailableAtPort(props.order.port?.country?.code, props.order.testOrder);
    const isSpotAllowed = role.isSpotAllowed(props.order.customerId);
    const updateOrderMutation = useOrderUpdate();

    const vesselDefaultSupplierId = props.order.vessel?.defaultSupplierId;
    const vesselDefaultContractId = props.order.vessel?.defaultContractId;

    const invitedSupplierIds = props.order.receiverSupplierIds;
    const assignmentSuppliers = props.assignments
        .map((assignment) => assignment.supplier)
        .filter((supplier): supplier is SupplierMessage => !!supplier);
    const notInvitedSuppliers = assignmentSuppliers.filter((supplier) => !invitedSupplierIds.includes(supplier.id));
    const contractSupplierIds = notInvitedSuppliers.map((supplier) => supplier.id);

    const contractsQuery = useQuery({
        ...queries.contracts.pagination({
            page: 0,
            supplierIds: contractSupplierIds,
            states: [ContractState.ACTIVE],
            vesselIds: props.order.vesselId ? [props.order.vesselId] : undefined,
        }),
        enabled: !!props.order.customerId && contractSupplierIds.length > 0,
        select: (page) => page.content,
    });

    const contractsBySupplierId = groupContractsBySupplier(
        contractsQuery.data ?? [],
        props.order.customerId,
        props.order.vesselId
    );

    const showSpotOption = spotAvailableAtPortQuery.data?.available === true && isSpotAllowed;
    const spotAlreadySelected = props.order.spot;

    const supplierEntries: SupplierEntry[] = notInvitedSuppliers.map((supplier) => ({
        supplier,
        contracts: sortContractsForSupplier(
            contractsBySupplierId[supplier.id] ?? [],
            supplier.id,
            vesselDefaultSupplierId,
            vesselDefaultContractId
        ),
        alreadyInvited: false,
    }));

    const getPreselectedContractId = (contracts: ContractMessage[]): string | undefined => {
        // contracts arrive sorted (vessel default first when applicable, then alphabetically),
        // so the first entry is the right preselection in every case.
        return contracts[0]?.id;
    };

    const onSubmit = (values: RequestSuppliersFormState) => {
        const mergedSupplierIds = [...invitedSupplierIds, ...values.selectedSupplierIds];
        const spot = spotAlreadySelected || values.spot;

        const newOrder: UpdateOrderRequest = {
            ...toUpdateOrderRequest(props.order),
            receiverSupplierIds: mergedSupplierIds,
            spot,
            cancelReason: null,
        };

        const newContractIds = values.selectedSupplierIds
            .map((supplierId) => values.contractIdBySupplierId[supplierId])
            .filter((contractId): contractId is string => !!contractId);

        // Drop any existing contract that belongs to a supplier being selected here so the
        // payload never carries more than one contract per supplier (the backend rejects that).
        const reselectedSupplierContractIds = new Set(
            values.selectedSupplierIds.flatMap((supplierId) =>
                (contractsBySupplierId[supplierId] ?? []).map((contract) => contract.id)
            )
        );
        const preservedContractIds = (props.order.receiverContractIds ?? []).filter(
            (contractId) => !reselectedSupplierContractIds.has(contractId)
        );

        newOrder.receiverContractIds = [...preservedContractIds, ...newContractIds];

        updateOrderMutation
            .mutateAsync({
                orderId: props.order.id,
                order: newOrder,
            })
            .then(() => {
                props.onClose();
            })
            .catch(() => {
                props.onClose(true);
            });
    };

    const isLoading = spotAvailableAtPortQuery.isLoading || contractsQuery.isLoading;

    if (isLoading) {
        return (
            <Modal isOpen={true} onDismiss={props.onClose} dismissible={false}>
                <Box padding={24} display="grid" rowGap={16}>
                    <Text variant="title">{translate('order.detail.requestSuppliers.title')}</Text>
                    <Text variant="small">{translate('order.detail.requestSuppliers.description')}</Text>
                    <Box display="grid" rowGap={6}>
                        <Skeleton height={24} />
                        <Skeleton height={24} />
                        <Skeleton height={24} />
                    </Box>
                    <Box display="flex" justifyContent="end">
                        <Button type="button" onClick={() => props.onClose()}>
                            {translate('order.detail.requestSuppliers.cancel')}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        );
    }

    return (
        <Modal isOpen={true} onDismiss={props.onClose} dismissible={false}>
            <Formik<RequestSuppliersFormState>
                initialValues={{
                    spot: spotAlreadySelected,
                    selectedSupplierIds: [],
                    contractIdBySupplierId: {},
                }}
                onSubmit={onSubmit}
            >
                {(formik) => {
                    const toggleSupplier = (supplier: SupplierMessage, contracts: ContractMessage[]) => {
                        if (formik.values.selectedSupplierIds.includes(supplier.id)) {
                            formik.setFieldValue(
                                'selectedSupplierIds',
                                formik.values.selectedSupplierIds.filter((id) => id !== supplier.id)
                            );
                            const nextContractIds = { ...formik.values.contractIdBySupplierId };
                            delete nextContractIds[supplier.id];
                            formik.setFieldValue('contractIdBySupplierId', nextContractIds);
                            return;
                        }

                        formik.setFieldValue('selectedSupplierIds', [
                            ...formik.values.selectedSupplierIds,
                            supplier.id,
                        ]);

                        const preselectedContractId = getPreselectedContractId(contracts);
                        if (preselectedContractId) {
                            formik.setFieldValue('contractIdBySupplierId', {
                                ...formik.values.contractIdBySupplierId,
                                [supplier.id]: preselectedContractId,
                            });
                        }
                    };

                    const selectContract = (supplierId: string, contractId: string) => {
                        formik.setFieldValue('contractIdBySupplierId', {
                            ...formik.values.contractIdBySupplierId,
                            [supplierId]: contractId,
                        });
                    };

                    const hasNewEntry =
                        formik.values.selectedSupplierIds.length > 0 || (!spotAlreadySelected && formik.values.spot);
                    const allSelectedHaveContract = formik.values.selectedSupplierIds.every(
                        (supplierId) =>
                            (contractsBySupplierId[supplierId]?.length ?? 0) === 0 ||
                            !!formik.values.contractIdBySupplierId[supplierId]
                    );
                    const canSubmit = hasNewEntry && allSelectedHaveContract;

                    return (
                        <Form>
                            <Box padding={24} display="grid" rowGap={16}>
                                <Text variant="title">{translate('order.detail.requestSuppliers.title')}</Text>

                                <Text variant="small">{translate('order.detail.requestSuppliers.description')}</Text>

                                <Box display="grid" rowGap={6}>
                                    <InputLabelText>
                                        {translate('order.detail.requestSuppliers.suppliers')}
                                    </InputLabelText>

                                    {showSpotOption ? (
                                        <Box marginBottom={8}>
                                            <Checkbox
                                                label={translate('order.detail.requestSuppliers.spot')}
                                                checked={spotAlreadySelected || formik.values.spot}
                                                disabled={spotAlreadySelected}
                                                onChange={() => formik.setFieldValue('spot', !formik.values.spot)}
                                            />
                                        </Box>
                                    ) : null}

                                    {supplierEntries.map((entry) => (
                                        <RequestSupplierOption
                                            key={entry.supplier.id}
                                            entry={entry}
                                            selected={formik.values.selectedSupplierIds.includes(entry.supplier.id)}
                                            selectedContractId={formik.values.contractIdBySupplierId[entry.supplier.id]}
                                            vesselDefaultSupplierId={vesselDefaultSupplierId}
                                            vesselDefaultContractId={vesselDefaultContractId}
                                            onToggle={() => toggleSupplier(entry.supplier, entry.contracts)}
                                            onSelectContract={(contractId) =>
                                                selectContract(entry.supplier.id, contractId)
                                            }
                                        />
                                    ))}
                                </Box>

                                <Box display="flex" justifyContent="end" gap={8}>
                                    <Button type="button" onClick={() => props.onClose()}>
                                        {translate('order.detail.requestSuppliers.cancel')}
                                    </Button>
                                    <Button
                                        type="submit"
                                        emphasis="high"
                                        disabled={!canSubmit}
                                        isLoading={updateOrderMutation.isPending}
                                    >
                                        {translate('order.detail.requestSuppliers.submit')}
                                    </Button>
                                </Box>
                            </Box>

                            <FormikDebug />
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};
